import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, User, Menu, Globe, ArrowLeft } from "lucide-react";
import ThomLogo from "@/components/ThomLogo";
import NotificationBell from "@/components/NotificationBell";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const dashboardPath = (role) => {
  if (role === "admin") return "/admin";
  if (role === "inspector") return "/inspector";
  if (role === "seller" || role === "dealer") return "/seller";
  return "/buyer";
};

const NAV_LINKS = [
  { to: "/marketplace",    label: "Vehicles",           testid: "nav-marketplace" },
  { to: "/property",       label: "Property",           testid: "nav-property" },
  { to: "/find-my-vehicle",label: "Find My Vehicle",    testid: "nav-fmv" },
  { to: "/advisor",        label: "Buyer Advisor",      testid: "nav-advisor" },
  { to: "/network",        label: "Trust Network",      testid: "nav-network" },
  { to: "/services",       label: "Services",           testid: "nav-services" },
  { to: "/verify",         label: "Verify Certificate", testid: "nav-verify" },
];

const ADMIN_LINKS = [
  { to: "/admin",              label: "Admin Dashboard",    testid: "nav-admin" },
  { to: "/ops",                label: "Executive Ops",      testid: "nav-ops" },
  { to: "/admin/verification-requests", label: "TV-AFR Triage", testid: "nav-triage" },
  { to: "/admin/countries",    label: "Country Config",     testid: "nav-country-config" },
  { to: "/command-centre",     label: "Command Centre",     testid: "nav-command" },
  { to: "/network/intelligence", label: "Network Intel",    testid: "nav-network-intel" },
  { to: "/geo-intelligence",   label: "African Trust Map",  testid: "nav-geo" },
];

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 bg-background border-b border-border">
      {/* Country identity ribbon — reinforces this is a country deployment */}
      <div className="bg-[#0A0A0A] text-white/70 text-[11px]" data-testid="country-ribbon">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 h-8 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 mono uppercase tracking-[0.22em] text-[10px]">
            <Globe className="h-3 w-3 text-[#B08C4A]" />
            <span>THOM Verify™ Zimbabwe · Zimbabwe country platform</span>
          </div>
          <Link to="/africa" className="mono uppercase tracking-[0.22em] text-[10px] text-white/60 hover:text-white inline-flex items-center gap-1" data-testid="ribbon-back-africa">
            <ArrowLeft className="h-3 w-3" /> THOM Verify Africa
          </Link>
        </div>
      </div>
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-4 md:px-6 h-20 gap-3">
        <Link
          to="/"
          data-testid="brand-home-link"
          className="group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm shrink-0"
          aria-label="THOM Verify home"
        >
          <ThomLogo size={40} showTagline data-testid="navbar-brand-logo" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7 text-[13px] tracking-wide">
          {NAV_LINKS.map(l => (
            <NavLink key={l.to} to={l.to} data-testid={l.testid} className={({ isActive }) => `hover:text-primary transition-colors ${isActive ? "text-primary" : ""}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {user && <NotificationBell variant="light" />}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" data-testid="nav-user-menu" className="gap-2">
                  <User className="h-4 w-4" />
                  <span className="hidden sm:inline">{user.name}</span>
                  <span className="text-[10px] mono uppercase tracking-wider text-muted-foreground">{user.role}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate(dashboardPath(user.role))} data-testid="nav-menu-dashboard">Dashboard</DropdownMenuItem>
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="nav-menu-logout">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login" className="hidden sm:inline-flex"><Button variant="outline" size="sm" data-testid="nav-login">Sign in</Button></Link>
              <Link to="/register" className="hidden sm:inline-flex"><Button size="sm" data-testid="nav-register" className="bg-primary hover:bg-primary/90">Get Started</Button></Link>
            </>
          )}

          {/* Mobile / tablet menu trigger — hidden at lg+ where the nav is inline */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Open menu" data-testid="nav-mobile-trigger">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] sm:w-[340px]" data-testid="nav-mobile-panel">
              <SheetHeader>
                <SheetTitle className="text-left">Menu</SheetTitle>
              </SheetHeader>
              <nav className="mt-6 flex flex-col divide-y divide-border">
                {NAV_LINKS.map(l => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`${l.testid}-mobile`}
                    className={({ isActive }) => `py-3 text-sm ${isActive ? "text-primary font-semibold" : "text-foreground/90"} hover:text-primary transition-colors`}
                  >
                    {l.label}
                  </NavLink>
                ))}
                {user?.role === "admin" && (
                  <div className="pt-4">
                    <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Administration</div>
                    {ADMIN_LINKS.map(l => (
                      <NavLink
                        key={l.to}
                        to={l.to}
                        onClick={() => setMobileOpen(false)}
                        data-testid={`${l.testid}-mobile`}
                        className={({ isActive }) => `block py-2.5 text-sm ${isActive ? "text-primary font-semibold" : "text-foreground/90"} hover:text-primary transition-colors`}
                      >
                        {l.label}
                      </NavLink>
                    ))}
                  </div>
                )}
                {!user && (
                  <div className="pt-4 flex flex-col gap-2">
                    <Link to="/login" onClick={() => setMobileOpen(false)}>
                      <Button variant="outline" className="w-full" data-testid="nav-login-mobile">Sign in</Button>
                    </Link>
                    <Link to="/register" onClick={() => setMobileOpen(false)}>
                      <Button className="w-full bg-primary hover:bg-primary/90" data-testid="nav-register-mobile">Get Started</Button>
                    </Link>
                  </div>
                )}
                {user && (
                  <div className="pt-4 flex flex-col gap-2">
                    <Button variant="outline" className="w-full" onClick={() => { setMobileOpen(false); navigate(dashboardPath(user.role)); }} data-testid="nav-menu-dashboard-mobile">Dashboard</Button>
                    <Button variant="ghost" className="w-full" onClick={async () => { setMobileOpen(false); await logout(); navigate("/"); }} data-testid="nav-menu-logout-mobile">
                      <LogOut className="h-4 w-4 mr-2" /> Sign out
                    </Button>
                  </div>
                )}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
